import { Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Subject } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import {
  YdsButtonComponent,
  YdsCardComponent,
  YdsDropdownComponent,
  YdsEmptyStatesComponent,
  YdsSpinnerComponent,
  YdsTableComponent,
  YdsSplitButtonComponent,
  YdsTableCellDirective,
  YdsPaginationComponent,
  type YdsDropdownOption,
  type YdsTableColumn,
  type YdsSplitButtonItem,
} from '../../../yds-angular';
import { formatDisplayDate } from '../../../shared/general-ledger-yds.utils';
import {
  GeneralLedgerAccountService,
  type GeneralLedgerAccountListItem,
  type GeneralLedgerOption,
} from '../generalledgeraccount.service';
import { ConfirmDialogService } from '../../../dialog/confirm-dialog/confirm-dialog.service';
import { deleteGeneralLedgerAccountWithChecks } from '../generalledgeraccount-shared';

@Component({
  selector: 'app-generalledgeraccount-list',
  imports: [
    YdsCardComponent,
    YdsButtonComponent,
    YdsDropdownComponent,
    YdsTableComponent,
    YdsSpinnerComponent,
    YdsEmptyStatesComponent,
    YdsSplitButtonComponent,
    YdsTableCellDirective,
    YdsPaginationComponent,
  ],
  templateUrl: './generalledgeraccount-list.component.html',
  styleUrl: './generalledgeraccount-list.component.scss',
})
export class GeneralledgeraccountListComponent implements OnInit {
  readonly pageSize = 10;
  readonly tableColumns: YdsTableColumn[] = [
    { key: 'generalLedger_Account', header: 'Nummer' },
    { key: 'generalLedger_Account_Name', header: 'Omschrijving' },
    { key: 'generalLedger_Group_Name', header: 'Grootboekrekeningengroep' },
    { key: 'kindOf_Account', header: 'Soort rekening' },
    { key: 'startDate', header: 'Begindatum' },
    { key: 'endDate', header: 'Einddatum' },
    { key: 'actions', header: 'Actieknop', width: '160px' },
  ];

  readonly splitButtonItems: readonly YdsSplitButtonItem[] = [
    { id: 'view', label: 'Bekijk details' },
    { id: 'edit', label: 'Wijzigen' },
    { id: 'generalledger', label: 'Grootboek' },
    { id: 'generalledgergroup', label: 'Grootboekrekeninggroep' },
    { id: 'copy', label: 'Kopiëren', disabled: true },
    { id: 'delete', label: 'Verwijderen' },
  ];

  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private accountService = inject(GeneralLedgerAccountService);
  private dialogService = inject(ConfirmDialogService);
  private destroyRef = inject(DestroyRef);

  generalLedgers: WritableSignal<GeneralLedgerOption[]> = signal([]);
  generalLedgerId = signal<string>('');
  isLoadingLedgers = signal(true);

  accounts: WritableSignal<GeneralLedgerAccountListItem[]> = signal([]);
  pageIndex = signal(0);
  isLoading = signal(true);

  generalLedgerOptions = computed<YdsDropdownOption[]>(() =>
    this.generalLedgers().map((ledger) => ({
      id: ledger.id,
      label: ledger.name,
    })),
  );

  private reload$ = new Subject<void>();

  pagedAccounts = computed(() => {
    const start = this.pageIndex() * this.pageSize;
    return this.accounts().slice(start, start + this.pageSize);
  });

  tableItems = computed(() =>
    this.pagedAccounts().map((item) => ({
      id: item.generalLedgerAccountId,
      generalLedger_Account: item.generalLedger_Account,
      generalLedger_Account_Name: item.generalLedger_Account_Name,
      generalLedger_Group_Name: item.generalLedger_Group_Name,
      kindOf_Account: item.kindOf_Account === 'CREDIT' ? 'Credit' : 'Debet',
      startDate: formatDisplayDate(item.startDate, 'nl-NL'),
      endDate: this.formatEndDate(item.endDate),
    })),
  );

  private formatEndDate(endDate: GeneralLedgerAccountListItem['endDate']): string {
    const formatted = formatDisplayDate(endDate, 'nl-NL');
    return formatted === '31-12-9999' ? 'n.v.t' : formatted;
  }

  constructor() {
    this.reload$
      .pipe(
        switchMap(() => {
          this.isLoading.set(true);
          this.accounts.set([]);
          const ledgerId = this.generalLedgerId();
          if (!ledgerId) {
            this.isLoading.set(false);
            return EMPTY;
          }
          return this.accountService.getByContext(ledgerId).pipe(
            catchError(() => {
              this.isLoading.set(false);
              this.accounts.set([]);
              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (items) => {
          this.accounts.set(items);
          this.pageIndex.set(0);
          this.isLoading.set(false);
        },
      });
  }

  ngOnInit(): void {
    this.loadGeneralLedgers();
  }

  loadGeneralLedgers(): void {
    this.isLoadingLedgers.set(true);
    this.accountService
      .getGeneralLedgersByContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (ledgers) => {
          this.generalLedgers.set(ledgers);
          this.isLoadingLedgers.set(false);
          if (ledgers.length > 0) {
            // Select the ledger from the route when present, otherwise the first one.
            const routeLedgerId = this.route.snapshot.paramMap.get('generalLedgerId');
            const selected = ledgers.find((ledger) => ledger.id === routeLedgerId);
            this.onGeneralLedgerChange((selected ?? ledgers[0]).id);
          } else {
            this.generalLedgerId.set('');
            this.isLoading.set(false);
          }
        },
        error: () => {
          this.generalLedgers.set([]);
          this.isLoadingLedgers.set(false);
          this.isLoading.set(false);
        },
      });
  }

  onGeneralLedgerChange(generalLedgerId: string | null): void {
    this.generalLedgerId.set(generalLedgerId ?? '');
    this.loadAccounts();
  }

  loadAccounts(): void {
    this.reload$.next();
  }

  navigateToCreate(): void {
    const ledgerId = this.generalLedgerId();
    this.router.navigate(
      ['/config/generalledgeraccount/editable'],
      ledgerId ? { queryParams: { generalLedgerId: ledgerId } } : undefined,
    );
  }

  onTableRowClick(row: Record<string, unknown>): void {
    // Default action when clicking a row is "Bekijk details".
    this.viewItem(String(row['id']));
  }

  viewItem(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.router.navigate(['/config/generalledgeraccount/editable', id]);
  }

  handleActionClick(id: string, actionId: string): void {
    if (actionId === 'view' || actionId === 'edit') {
      this.viewItem(id);
      return;
    }
    if (actionId === 'generalledger') {
      const ledgerId = this.generalLedgerId();
      if (ledgerId) {
        this.router.navigate(['/config/generalledger/editable', ledgerId]);
      } else {
        this.router.navigate(['/config/generalledger']);
      }
      return;
    }
    if (actionId === 'generalledgergroup') {
      const groupId = this.accounts().find((a) => a.generalLedgerAccountId === id)
        ?.generalLedger_Group_ID;
      if (groupId) {
        this.router.navigate(['/config/generalledgergroup/editable', groupId]);
      } else {
        this.router.navigate(['/config/generalledgergroup']);
      }
      return;
    }
    if (actionId === 'delete') {
      this.deleteItem(id);
      return;
    }
  }

  private deleteItem(id: string): void {
    deleteGeneralLedgerAccountWithChecks({
      id,
      service: this.accountService,
      dialogService: this.dialogService,
      destroyRef: this.destroyRef,
      onDeleted: () => this.loadAccounts(),
    });
  }
}
